import { useEffect, useRef } from 'react';
import { streem } from 'streem-sdk-protobuf';
import { useGetCompanyStore } from '../../../hooks/detail_store_hooks';
import { useReservationBannerHandler } from '../../../hooks/useReservationBannerHandler';
import {
    useCompanySettingsStore,
    useCurrentCallSourceStore,
    useSDKStore,
    useUIStore,
} from '../../../hooks/use_global_context';

import { LegacyReservationBanner } from '../legacy_reservation_banner';
import appLogger from '../../../util/logging/app_logger';

import { convertProtobufToSeconds } from '../util/group_reservation_helpers';
import { ReservationBanner } from '../reservation_banner';
import { AppText, ToastTypes } from '@streem/ui-react';
import { StreemAPI } from '@streem/api';
import { defaultNotAvailableOption } from '../util/default_availability_selection';
import type { StatusOptionValueType } from '../status_option_value';

type GroupReservationReturn = {
    isReserved: boolean;
    isInCall: boolean;
};

const AvailabilityStatus = streem.api.AvailabilityStatus;
const GroupReservationStatus = streem.api.GroupReservationStatus;
const ReservationType = streem.api.GroupReservation.ReservationType;
const log = appLogger.extend('UseGroupReservation');

export const useGroupReservation = (
    companyCode: string,
    availabilityStatus: streem.api.AvailabilityStatus,
    setSelectedStatus: React.Dispatch<React.SetStateAction<Readonly<StatusOptionValueType>>>,
): GroupReservationReturn => {
    const uiStore = useUIStore();
    const sdkStore = useSDKStore();
    const companySettingsStore = useCompanySettingsStore();
    const companyStore = useGetCompanyStore();
    const currentCallSourceStore = useCurrentCallSourceStore();

    // locks known stable references as static for effects
    // this helps keep our dependency arrays clean to reflect the actual triggers for change
    const uiStoreRef = useRef(uiStore);
    const setSelectedStatusRef = useRef(setSelectedStatus);
    const currentCallSourceStoreRef = useRef(currentCallSourceStore);

    const groupReservationWallItem = sdkStore?.groupReservation?.current;
    const groupReservationStatus = groupReservationWallItem?.reservationStatus;

    const { shouldShowBanner, shouldClearBanner } = useReservationBannerHandler(
        availabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_RESERVED,
        groupReservationStatus,
    );

    const isReservedInGroup = [
        GroupReservationStatus.GROUP_RESERVATION_STATUS_MATCHED,
        GroupReservationStatus.GROUP_RESERVATION_STATUS_CONFIRMING,
        GroupReservationStatus.GROUP_RESERVATION_STATUS_INITIATED,
    ].includes(groupReservationStatus);

    const isInCall =
        groupReservationStatus === GroupReservationStatus.GROUP_RESERVATION_STATUS_CONNECTED;

    const isConfirming =
        availabilityStatus === AvailabilityStatus.AVAILABILITY_STATUS_CONFIRMING &&
        groupReservationStatus === GroupReservationStatus.GROUP_RESERVATION_STATUS_CONFIRMING;
    const groupName = groupReservationWallItem?.group?.name;
    const reservationSid = groupReservationWallItem?.reservationSid;

    useEffect(() => {
        if (isConfirming) {
            const confirmMatchedGroupReservation = async (
                companyCode: string,
                groupName: string,
                reservationSid: string,
            ) => {
                log.info('Confirming matched group reservation');
                try {
                    await StreemAPI.companies.confirmMatchedGroupReservation(
                        companyCode,
                        groupName,
                        reservationSid,
                    );
                } catch (e) {
                    setSelectedStatusRef.current(defaultNotAvailableOption);
                    log.error('There was an error confirming the group reservation', e);
                }
            };

            confirmMatchedGroupReservation(companyCode, groupName, reservationSid);
        }
    }, [isConfirming, companyCode, groupName, reservationSid]);

    /**
     * Group Reservation Logic
     */
    useEffect(() => {
        if (shouldShowBanner && !uiStore.bannerNotificationContent) {
            if (
                companySettingsStore.ahsVirtualExpertEnabled &&
                groupReservationWallItem.callDetail
            ) {
                log.info(
                    `Showing Upcoming Call banner for reservation: ${groupReservationWallItem.reservationSid}`,
                );

                currentCallSourceStoreRef.current.setGroupReservationCallSource(
                    companyStore.result?.callSources,
                );
                uiStoreRef.current.addBannerNotification(() => (
                    <ReservationBanner
                        reservationCallDetail={groupReservationWallItem.callDetail}
                        reservationCallSource={
                            currentCallSourceStoreRef.current.groupReservationCallSource
                        }
                        expirationTime={groupReservationWallItem.reservedUntil}
                        secondsReserved={groupReservationWallItem.reserveForSeconds}
                        isReconnect={
                            groupReservationWallItem.reservationType ===
                            ReservationType.RESERVATION_TYPE_RECONNECT
                        }
                        isRequeue={
                            groupReservationWallItem.reservationType ===
                            ReservationType.RESERVATION_TYPE_REQUEUE
                        }
                        groupName={groupReservationWallItem.group.name}
                        reservationSid={groupReservationWallItem.reservationSid}
                    />
                ));
            } else {
                log.info(
                    `Showing Legacy Upcoming Call banner for reservation: ${groupReservationWallItem.reservationSid}`,
                );
                uiStoreRef.current.addBannerNotification(() => (
                    <LegacyReservationBanner
                        expirationTime={groupReservationWallItem.reservedUntil}
                        secondsReserved={groupReservationWallItem.reserveForSeconds}
                        customerDetails={groupReservationWallItem.details}
                        isReconnect={!!groupReservationWallItem.reconnectPreviousReservationSid}
                        reservationSid={groupReservationWallItem.reservationSid}
                    />
                ));
            }
        }

        if (shouldClearBanner && uiStore.bannerNotificationContent) {
            log.info('Removing reservation banner');
            uiStoreRef.current.clearBannerNotification();
        }
    }, [
        uiStore.bannerNotificationContent,
        groupReservationWallItem,
        companySettingsStore.ahsVirtualExpertEnabled,
        companyStore.result?.callSources,
        shouldShowBanner,
        shouldClearBanner,
    ]);

    useEffect(() => {
        // Define now for each render to avoid microsecond race conditions
        const nowInSeconds = Date.now() / 1000;
        // Show a toast if the user cancels the reservation, but make sure it's not an old reservation that's still the most recent one
        const isGroupReservationCancelledBeforeItHasExpired =
            groupReservationWallItem?.reservationStatus ===
                GroupReservationStatus.GROUP_RESERVATION_STATUS_CANCELED &&
            convertProtobufToSeconds(groupReservationWallItem?.reservedUntil) > nowInSeconds;

        // Only show the reservation expired toast within 5 seconds of the expiration time.  Expired reservations
        // remain on the wall indefinitely (until a new reservation is made for the expert), but we don't want to
        // show the toast for a reservation that expired longer ago.
        const isGroupReservationRecentlyExpired =
            groupReservationWallItem?.reservationStatus ===
                GroupReservationStatus.GROUP_RESERVATION_STATUS_EXPIRED &&
            convertProtobufToSeconds(groupReservationWallItem?.reservedUntil) + 5 > nowInSeconds;

        if (isGroupReservationCancelledBeforeItHasExpired) {
            uiStore.addToast({
                flavor: ToastTypes.WARNING,
                id: `group-reservation-canceled-${groupReservationWallItem.id}`,
                content: (
                    <AppText>
                        Your upcoming call was canceled. <br />
                        Your status has been set back to Ready.
                    </AppText>
                ),
            });
        } else if (isGroupReservationRecentlyExpired) {
            uiStore.addToast({
                flavor: ToastTypes.WARNING,
                id: `group-reservation-canceled-${groupReservationWallItem.id}`,
                content: (
                    <AppText>
                        The reservation expired.
                        <br />
                        Your status has been set back to Ready.
                    </AppText>
                ),
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupReservationWallItem?.reservationStatus]);

    useEffect(() => {
        const disposer =
            currentCallSourceStoreRef.current.clearCallSourceDetailsOnCancelledOrExpiredOrDeclined();

        return () => disposer();
    }, []);

    return { isReserved: isReservedInGroup, isInCall };
};
